// ============================================================================
// RECORD / CORRECT A CLIENT'S AGGREGATE TURNOVER (ADR-0021 §4)
// ============================================================================
// Per CLIENT and per financial year, never per registration: aggregate turnover
// is computed across all GSTINs of a PAN, all-India.
//
// The client and year identify the fact, so in edit mode they are shown and not
// editable — moving either would re-target the figure at a different client or
// year rather than correcting this one.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { SyutClientSelect } from 'components/ui/SyutEntitySelect';
import notificationService from 'services/notificationService';
import { gstTurnoverService } from 'api/services/gstTurnoverService';
import type { GstTurnover } from 'api/types/gstTurnover.types';

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /** The row being corrected, in edit mode. */
  record?: GstTurnover | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Where the figure came from. Required rather than defaulted silently, because
 * it decides how much weight a warning deserves: a number the client mentioned
 * and one from audited accounts should not prompt with equal confidence.
 */
const SOURCE_OPTIONS = [
  { value: 'DECLARED', label: 'Declared — the client told us' },
  { value: 'AUDITED', label: 'Audited — from audited accounts' },
  { value: 'COMPUTED', label: 'Computed — derived from returns we hold' },
];

const fyLabel = (year: number) => `FY ${year}-${String((year + 1) % 100).padStart(2, '0')}`;

/** The financial year now in progress. April is statutory for GST. */
const currentFinancialYear = (): number => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

/**
 * Crore and lakh, because that is how the figure is quoted and how every
 * threshold is written. Showing 50,000,000 makes the reader count digits to
 * check it against a limit they know as "5 crore".
 */
const inWords = (rupees: number): string => {
  if (!Number.isFinite(rupees) || rupees <= 0) return '';
  if (rupees >= 1e7) return `${(rupees / 1e7).toFixed(2).replace(/\.00$/, '')} crore`;
  if (rupees >= 1e5) return `${(rupees / 1e5).toFixed(2).replace(/\.00$/, '')} lakh`;
  return '';
};

interface FormState {
  clientId: string;
  financialYear: number;
  aggregateTurnover: string;
  source: string;
  notes: string;
}

const GstTurnoverModal: React.FC<Props> = ({ isOpen, mode, record, onClose, onSaved }) => {
  const initial = useMemo<FormState>(() => ({
    clientId: '',
    financialYear: currentFinancialYear() - 1,
    aggregateTurnover: '',
    source: 'DECLARED',
    notes: '',
  }), []);

  const [form, setForm] = useState<FormState>(initial);
  const snapshotRef = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const recordRef = useRef(record);
  recordRef.current = record;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors((prev) => {
      if (!prev[f]) return prev;
      const next = { ...prev };
      delete next[f];
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const r = recordRef.current;
    const data: FormState = r && modeRef.current === 'edit'
      ? {
        clientId: r.clientId,
        financialYear: r.financialYear,
        aggregateTurnover: String(r.aggregateTurnover ?? ''),
        source: r.source,
        notes: r.notes ?? '',
      }
      : { ...initial };

    setForm(data);
    snapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!snapshotRef.current) return false;
    return JSON.stringify(form) !== snapshotRef.current;
  }, [form]);

  const patch = useCallback((changes: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...changes }));
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  // The years a figure may be recorded for. GST began in 2017-18, and a year
  // that has not started cannot have a turnover.
  const yearOptions = useMemo(() => {
    const latest = currentFinancialYear();
    const years: { value: string; label: string }[] = [];
    for (let y = latest; y >= 2017; y -= 1) years.push({ value: String(y), label: fyLabel(y) });
    return years;
  }, []);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};

    if (mode === 'create' && !form.clientId) errors.clientId = 'Choose the client';
    if (!form.source) errors.source = 'Say where the figure came from';

    const amount = Number(form.aggregateTurnover);
    if (form.aggregateTurnover.trim() === '') {
      errors.aggregateTurnover = 'Enter the aggregate turnover';
    } else if (!Number.isFinite(amount)) {
      errors.aggregateTurnover = 'Enter a number';
    } else if (amount < 0) {
      // Zero is legitimate — a dormant company files nil returns — so the floor
      // is 0 rather than 1.
      errors.aggregateTurnover = 'Turnover cannot be negative';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.querySelector<HTMLElement>('input')?.focus();
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form, mode]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      if (mode === 'edit' && record) {
        await gstTurnoverService.update(record.id, {
          aggregateTurnover: Number(form.aggregateTurnover),
          source: form.source,
          notes: form.notes.trim() || null,
          rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Turnover for ${fyLabel(form.financialYear)} updated.`);
      } else {
        await gstTurnoverService.create({
          clientId: form.clientId,
          financialYear: form.financialYear,
          aggregateTurnover: Number(form.aggregateTurnover),
          source: form.source,
          notes: form.notes.trim() || null,
        });
        notificationService.toast.success(`Turnover for ${fyLabel(form.financialYear)} recorded.`);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not save the turnover figure.',
      );
    } finally {
      setIsSaving(false);
    }
  }, [form, mode, record, onSaved, onClose]);

  if (!isOpen) return null;

  const words = inWords(Number(form.aggregateTurnover));

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-md">
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving…
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'edit' ? 'Correct turnover' : 'Record turnover'}
          </h2>
          <button className="modal-close" onClick={handleClose} title="Close">
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div
            style={{
              border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
              borderRadius: 6, padding: '0.625rem 0.75rem', marginBottom: 14,
              fontSize: '0.8125rem', color: 'var(--text-secondary)',
            }}
          >
            Aggregate turnover across <strong>all GSTINs of the PAN, all-India</strong> — including
            exempt and non-taxable supplies. It pre-fills HSN digits and flags QRMP, e-invoicing and
            GSTR-9C, but never blocks anything.
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="clientId">
              {mode === 'edit' ? (
                <>
                  <label className="form-label">Client</label>
                  <input
                    type="text"
                    className="form-input"
                    value={record?.clientLegalName ?? ''}
                    disabled
                  />
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                    The client and year identify this figure, so they cannot be moved. Record a
                    separate row for another year.
                  </div>
                </>
              ) : (
                <SyutClientSelect
                  label={<>Client <span className="text-danger">*</span></>}
                  value={form.clientId}
                  onChange={(id) => { clearFieldError('clientId'); patch({ clientId: id }); }}
                  error={getFieldError('clientId')}
                  size="sm"
                />
              )}
            </div>

            <div className="form-group" data-field="financialYear">
              {mode === 'edit' ? (
                <>
                  <label className="form-label">Financial year</label>
                  <input type="text" className="form-input" value={fyLabel(form.financialYear)} disabled />
                </>
              ) : (
                <SyutSelect
                  label={<>Financial year <span className="text-danger">*</span></>}
                  options={yearOptions}
                  value={String(form.financialYear)}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => patch({ financialYear: Number(v) || currentFinancialYear() - 1 })}
                />
              )}
              {mode === 'create' && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                  The year the turnover RELATES TO. Assessing {fyLabel(form.financialYear + 1)} reads
                  this figure.
                </div>
              )}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="aggregateTurnover">
              <label className="form-label">
                Aggregate turnover (₹) <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                className={`form-input${getFieldError('aggregateTurnover') ? ' form-field-error' : ''}`}
                value={form.aggregateTurnover}
                onChange={(e) => {
                  clearFieldError('aggregateTurnover');
                  patch({ aggregateTurnover: e.target.value });
                }}
              />
              {getFieldError('aggregateTurnover')
                ? <div className="form-error-message">{getFieldError('aggregateTurnover')}</div>
                : (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                    {words
                      ? <>In full rupees — <strong>{words}</strong>.</>
                      : 'In full rupees, not crore. Zero is valid for a dormant client.'}
                  </div>
                )}
            </div>

            <div className="form-group" data-field="source">
              <SyutSelect
                label={<>Source <span className="text-danger">*</span></>}
                options={SOURCE_OPTIONS}
                value={form.source}
                isMulti={false}
                isClearable={false}
                size="sm"
                error={getFieldError('source')}
                onChange={(v) => { clearFieldError('source'); patch({ source: (v as string) || 'DECLARED' }); }}
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                Shown beside every warning this figure produces, so the reader can weigh it.
              </div>
            </div>
          </div>

          <div className="form-group" data-field="notes">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={form.notes}
              placeholder="Where the figure came from, or what it is pending."
              onChange={(e) => patch({ notes: e.target.value })}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {mode === 'edit' ? 'Save' : 'Record'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Save this correction?' : 'Record this turnover?'}
        message={
          `${words ? `${words} ` : ''}for ${fyLabel(form.financialYear)}, as ${form.source.toLowerCase()}. `
          + 'This changes what QRMP, e-invoicing, GSTR-9C and HSN digits are suggested — none of which blocks anything.'
        }
        confirmLabel={mode === 'edit' ? 'Save' : 'Record'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void handleConfirmSave(); }}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default GstTurnoverModal;
